/**
 * @module CatalogueEntry
 * @purpose One entry in the persistent hypothesis catalogue sidebar: display label,
 *   version/kind meta, summary, clamped description, artefact-type chips, and MITRE chips.
 *   Deliberately named "Catalogue*" (not "Library*") to avoid conflating this static
 *   platform-wide entry with the per-case coverage LibraryPanelRow.
 * @depends types/reasoning (HypothesisLibraryEntry), attack-techniques (techniqueName), react-i18next
 * @dependedOnBy CatalogueSidebar
 *
 * @rules
 * - Performance: pure display — all data via props, no data fetching
 * - State: none
 * - INVARIANT: descriptive metadata only — no score, rank, or confidence anywhere.
 */

import { useTranslation } from 'react-i18next'
import type { HypothesisLibraryEntry } from '../../../../../types/reasoning'
import { techniqueName } from './attack-techniques'

interface CatalogueEntryProps {
  entry: HypothesisLibraryEntry
}

const DESCRIPTION_CLAMP_LINES = 2

const ENTRY_STYLE: React.CSSProperties = {
  padding: '10px 12px',
  borderTop: '1px solid var(--border)',
}

const LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: 'var(--text)',
}

const META_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  color: 'var(--text-muted)',
  marginTop: '2px',
}

const SUMMARY_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text)',
  marginTop: '6px',
}

const DESCRIPTION_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.72rem',
  color: 'var(--text-dim)',
  marginTop: '4px',
  display: '-webkit-box',
  WebkitLineClamp: DESCRIPTION_CLAMP_LINES,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}

const CHIP_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
  marginTop: '6px',
}

const CHIP_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  background: 'transparent',
}

/**
 * A single catalogue entry.
 *
 * @behaviour Renders displayLabel, a version/kind meta line, the summary prominently, the
 *   description clamped to two lines (full text via the title attribute), one chip per
 *   artefact type, and one chip per MITRE ref ("{id} — {name}"). Renders no MITRE row when
 *   mitreRefs is empty. No score/rank/confidence anywhere.
 * @errors n/a
 */
export function CatalogueEntry({ entry }: CatalogueEntryProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div style={ENTRY_STYLE}>
      <div style={LABEL_STYLE}>{entry.displayLabel}</div>
      <div style={META_STYLE}>
        {t('reasoning.hypothesis.catalogue.versionLabel')} {entry.version} · {t('reasoning.hypothesis.catalogue.kindLabel')} {entry.kind.replaceAll('_', ' ')}
      </div>
      <div style={SUMMARY_STYLE}>{entry.summary}</div>
      <div style={DESCRIPTION_STYLE} title={entry.description}>{entry.description}</div>
      <div style={CHIP_ROW_STYLE} aria-label={t('reasoning.hypothesis.catalogue.artefactTypesLabel')}>
        {entry.artefactTypes.map((artefactType) => (
          <span key={artefactType} style={CHIP_BASE}>{artefactType}</span>
        ))}
      </div>
      {entry.mitreRefs.length > 0 && (
        <div style={CHIP_ROW_STYLE} aria-label={t('reasoning.hypothesis.catalogue.mitreLabel')}>
          {entry.mitreRefs.map((id) => (
            <span key={id} style={CHIP_BASE}>{id} — {techniqueName(id)}</span>
          ))}
        </div>
      )}
    </div>
  )
}
